import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Inbox as InboxIcon,
	Mail,
	MailOpen,
	Archive,
	ArchiveRestore,
	Trash2,
	Search,
	Send,
	Webhook,
	Copy,
	CheckCircle2,
	AlertCircle,
	Paperclip,
	Calendar,
	RefreshCw,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { inboxAPI } from '@/services/api';
import { toast } from 'sonner';

const STATUS_TABS = [
	{ value: 'inbox', icon: InboxIcon },
	{ value: 'unread', icon: Mail },
	{ value: 'archived', icon: Archive },
	{ value: 'all', icon: MailOpen },
];

export default function InboxAdminManager() {
	const { t } = useTranslation();
	const [items, setItems] = useState([]);
	const [counts, setCounts] = useState({ inbox: 0, unread: 0, archived: 0 });
	const [recipients, setRecipients] = useState([]);
	const [loading, setLoading] = useState(true);

	const [status, setStatus] = useState('inbox');
	const [recipient, setRecipient] = useState('all');
	const [search, setSearch] = useState('');
	const [selectedId, setSelectedId] = useState(null);
	const [selected, setSelected] = useState(null);

	const [webhookOpen, setWebhookOpen] = useState(false);
	const [webhookCfg, setWebhookCfg] = useState(null);

	const [testOpen, setTestOpen] = useState(false);
	const [testForm, setTestForm] = useState({
		from_email: 'visitor@example.com',
		to_email: 'admin@equi-market.eu',
		subject: '',
		body: '',
	});
	const [injecting, setInjecting] = useState(false);

	const reload = useCallback(async () => {
		setLoading(true);
		try {
			const params = { status, limit: 200 };
			if (recipient !== 'all') params.recipient = recipient;
			if (search.trim()) params.search = search.trim();
			const r = await inboxAPI.list(params);
			setItems(r.data?.items || []);
			setCounts(r.data?.counts || { inbox: 0, unread: 0, archived: 0 });
			setRecipients(r.data?.recipients || []);
		} catch {
			toast.error(t('common.error'));
		}
		setLoading(false);
	}, [status, recipient, search, t]);

	useEffect(() => { reload(); }, [reload]);

	const openMessage = async (id) => {
		setSelectedId(id);
		try {
			const r = await inboxAPI.get(id);
			setSelected(r.data);
			// Update the row's read state locally without a full refetch
			setItems((prev) => prev.map((it) => (it.id === id ? { ...it, is_read: true } : it)));
			setCounts((c) => ({ ...c, unread: Math.max(0, c.unread - (r.data?.is_read ? 0 : 1)) }));
		} catch {
			toast.error(t('common.error'));
		}
	};

	const toggleArchive = async (id, archive) => {
		try {
			await inboxAPI.update(id, { is_archived: archive });
			toast.success(t('common.success'));
			if (selectedId === id) { setSelectedId(null); setSelected(null); }
			reload();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const toggleRead = async (id, read) => {
		try {
			await inboxAPI.update(id, { is_read: read });
			reload();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const remove = async (id) => {
		if (!window.confirm(t('admin.inbox_delete_confirm'))) return;
		try {
			await inboxAPI.remove(id);
			toast.success(t('common.success'));
			if (selectedId === id) { setSelectedId(null); setSelected(null); }
			reload();
		} catch {
			toast.error(t('common.error'));
		}
	};

	const openWebhook = async () => {
		setWebhookOpen(true);
		try {
			const r = await inboxAPI.webhookConfig();
			setWebhookCfg(r.data);
		} catch {
			setWebhookCfg(null);
		}
	};

	const submitTest = async (e) => {
		e.preventDefault();
		if (!testForm.subject.trim() || !testForm.body.trim()) return;
		setInjecting(true);
		try {
			await inboxAPI.injectTest(testForm);
			toast.success(t('common.success'));
			setTestOpen(false);
			setTestForm((p) => ({ ...p, subject: '', body: '' }));
			reload();
		} catch {
			toast.error(t('common.error'));
		} finally {
			setInjecting(false);
		}
	};

	const fmtDate = (iso) => (iso || '').slice(0, 16).replace('T', ' ');

	const formatBytes = (n) => {
		if (!n) return '';
		if (n < 1024) return `${n} B`;
		if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
		return `${(n / (1024 * 1024)).toFixed(1)} MB`;
	};

	const downloadAttachment = async (messageId, index, filename) => {
		try {
			const res = await inboxAPI.downloadAttachment(messageId, index);
			const blob = new Blob([res.data], {
				type: res.headers?.['content-type'] || 'application/octet-stream',
			});
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename || `attachment-${index}`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			// free memory after the click handler has a chance to run
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		} catch (err) {
			toast.error(err?.response?.data?.detail || t('admin.inbox_attachment_not_downloadable'));
		}
	};

	return (
		<div className="space-y-4" data-testid="admin-inbox-manager">
			{/* Toolbar */}
			<div className="flex items-center justify-between gap-3 flex-wrap">
				<div className="text-sm text-[#666055] inline-flex items-center gap-2">
					<InboxIcon className="w-4 h-4 text-[#8B4513]" />
					{counts.inbox} {t('admin.inbox')} · {counts.unread} {t('admin.inbox_unread').toLowerCase()}
				</div>
				<div className="flex items-center gap-2 flex-wrap">
					<Button size="sm" variant="outline" onClick={reload}
									className="border-[#E6E2D6] text-[#1A3C34] rounded-sm h-9 text-xs"
									data-testid="admin-inbox-refresh-btn">
						<RefreshCw className="w-3 h-3 mr-1" /> {t('admin.inbox_refresh')}
					</Button>
					<Button size="sm" variant="outline" onClick={openWebhook}
									className="border-[#E6E2D6] text-[#1A3C34] rounded-sm h-9 text-xs"
									data-testid="admin-inbox-webhook-btn">
						<Webhook className="w-3 h-3 mr-1" /> {t('admin.inbox_webhook_setup')}
					</Button>
					<Button size="sm" onClick={() => setTestOpen(true)}
									className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-9 text-xs"
									data-testid="admin-inbox-test-btn">
						<Send className="w-3 h-3 mr-1" /> {t('admin.inbox_inject_test')}
					</Button>
				</div>
			</div>

			{/* Status tabs */}
			<div className="flex items-center gap-2 flex-wrap">
				{STATUS_TABS.map(({ value, icon: Icon }) => (
					<button
						key={value}
						type="button"
						onClick={() => { setStatus(value); setSelectedId(null); setSelected(null); }}
						className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border transition-colors ${
							status === value
								? 'bg-[#1A3C34] text-white border-[#1A3C34]'
								: 'bg-white text-[#1A3C34] border-[#E6E2D6] hover:bg-[#F2F0E9]'
						}`}
						data-testid={`admin-inbox-tab-${value}`}
					>
						<Icon className="w-3 h-3" /> {t(`admin.inbox_tab_${value}`)}
						{value === 'unread' && counts.unread > 0 && (
							<span className="ml-1 bg-[#9B2C2C] text-white text-[10px] px-1.5 rounded-sm">{counts.unread}</span>
						)}
						{value === 'archived' && counts.archived > 0 && (
							<span className={`ml-1 text-[10px] px-1.5 rounded-sm 
									${status === 'archived' ? 'bg-white/20' : 'bg-[#E6E2D6] text-[#666055]'}`}>
								{counts.archived}
							</span>
						)}
					</button>
				))}

				<div className="flex items-center gap-2 ml-auto flex-wrap">
					<Select value={recipient} onValueChange={(v) => { setRecipient(v); setSelectedId(null); }}>
						<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-9 text-xs w-56"
													 data-testid="admin-inbox-recipient-filter">
							<SelectValue placeholder={t('admin.inbox_all_recipients')} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">{t('admin.inbox_all_recipients')}</SelectItem>
							{recipients.map((r) => (
								<SelectItem key={r.email} value={r.email}>{r.email} ({r.count})</SelectItem>
							))}
						</SelectContent>
					</Select>
					<div className="relative">
						<Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#666055]" />
						<Input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={t('admin.inbox_search_placeholder')}
							className="bg-white border-[#E6E2D6] rounded-sm h-9 text-xs pl-7 w-64"
							data-testid="admin-inbox-search"
						/>
					</div>
				</div>
			</div>

			{/* List + preview */}
			<div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4 min-h-[420px]">
				{/* List */}
				<div className="bg-white border border-[#E6E2D6] rounded-sm overflow-hidden">
					{loading ? (
						<div className="p-6 text-sm text-[#666055]">Loading...</div>
					) : items.length === 0 ? (
						<div className="p-10 text-center text-sm text-[#666055]" data-testid="admin-inbox-empty">
							{t('admin.inbox_empty')}
						</div>
					) : (
						<ul className="divide-y divide-[#E6E2D6] max-h-[600px] overflow-y-auto">
							{items.map((m) => (
								<li
									key={m.id}
									onClick={() => openMessage(m.id)}
									className={`px-4 py-3 cursor-pointer hover:bg-[#F9F7F2] transition-colors ${
										selectedId === m.id ? 'bg-[#F2F0E9]' : ''
									} ${m.is_read ? '' : 'border-l-4 border-l-[#1A3C34]'}`}
									data-testid={`admin-inbox-row-${m.id}`}
								>
									<div className="flex items-center justify-between gap-2 mb-1">
                    <span className={`text-xs truncate flex-1 ${m.is_read ? 'text-[#666055]' : 'text-[#1A3C34] font-semibold'}`}>
                      {m.from_display || m.from_email || '—'}
                    </span>
										<span className="text-[10px] text-[#666055]/70 flex-shrink-0">{fmtDate(m.received_at)}</span>
									</div>
									<div className={`text-sm truncate mb-1 ${m.is_read ? 'text-[#666055]' : 'text-[#1A3C34] font-semibold'}`}>
										{m.subject || `(${t('admin.inbox_no_subject')})`}
									</div>
									<div className="flex items-center gap-2 flex-wrap">
										<span className="text-[10px] bg-[#F2F0E9] text-[#666055] px-2 py-0.5 rounded-sm">
											→ {m.primary_recipient || '—'}
										</span>
										{(m.attachments?.length || 0) > 0 && (
											<span className="text-[10px] text-[#8B4513] inline-flex items-center gap-0.5">
                        <Paperclip className="w-3 h-3" /> {m.attachments.length}
                      </span>
										)}
									</div>
								</li>
							))}
						</ul>
					)}
				</div>

				{/* Preview */}
				<div className="bg-white border border-[#E6E2D6] rounded-sm p-5">
					{!selected ? (
						<div className="h-full min-h-[300px] flex flex-col items-center justify-center text-center text-sm text-[#666055]/80"
								 data-testid="admin-inbox-no-selection">
							<Mail className="w-10 h-10 mb-3 text-[#E6E2D6]" />
							{t('admin.inbox_select_a_message')}
						</div>
					) : (
						<div data-testid={`admin-inbox-preview-${selected.id}`}>
							<h3 className="text-lg font-medium text-[#1A3C34] font-['Playfair_Display'] mb-2">
								{selected.subject || `(${t('admin.inbox_no_subject')})`}
							</h3>
							<div className="space-y-1 text-xs text-[#666055] mb-4 pb-4 border-b border-[#E6E2D6]">
								<div>
									<span className="text-[#666055]/70 uppercase tracking-wider mr-2">
										{t('admin.inbox_from')}:
									</span>
									<strong className="text-[#1A3C34]">{selected.from_display}</strong>
								</div>
								<div>
									<span className="text-[#666055]/70 uppercase tracking-wider mr-2">
										{t('admin.inbox_to')}:
									</span>
									{selected.to_display}
								</div>
								{selected.cc_display && (
									<div>
										<span className="text-[#666055]/70 uppercase tracking-wider mr-2">
											CC:
										</span>
										{selected.cc_display}
									</div>
								)}
								<div className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />
									{fmtDate(selected.received_at)}
								</div>
							</div>

							{selected.attachments?.length > 0 && (
								<div className="mb-4">
									<div className="text-[10px] uppercase tracking-wider text-[#666055]/70 mb-2">
										{t('admin.inbox_attachments')}
									</div>
									<div className="flex flex-wrap gap-2">
										{selected.attachments.map((a, i) => {
											const label = (
												<>
													<Paperclip className="w-3 h-3" />
													<span className="truncate max-w-[220px]">{a.filename || 'attachment'}</span>
													{a.size > 0 && (
														<span className="text-[10px] opacity-70">({formatBytes(a.size)})</span>
													)}
												</>
											);
											if (a.downloadable && !a.url) {
												return (
													<button
														key={i}
														type="button"
														onClick={() => downloadAttachment(selected.id, i, a.filename)}
														className="text-[11px] bg-[#F2F0E9] text-[#1A3C34] px-2 py-1 rounded-sm inline-flex
																items-center gap-1 hover:bg-[#E6E2D6] border border-[#E6E2D6] transition-colors"
														data-testid={`admin-inbox-attachment-download-${i}`}
														title={t('admin.inbox_attachment_download')}
													>
														{label}
													</button>
												);
											}
											if (a.url) {
												return (
													<a
														key={i}
														href={a.url}
														target="_blank"
														rel="noopener noreferrer"
														className="text-[11px] bg-[#F2F0E9] text-[#1A3C34] px-2 py-1 rounded-sm inline-flex
															items-center gap-1 hover:bg-[#E6E2D6] border border-[#E6E2D6] transition-colors"
														data-testid={`admin-inbox-attachment-link-${i}`}
													>
														{label}
													</a>
												);
											}
											return (
												<span
													key={i}
													className="text-[11px] bg-[#F2F0E9] text-[#666055]/70 px-2 py-1 rounded-sm inline-flex
														items-center gap-1 border border-dashed border-[#E6E2D6]"
													title={t('admin.inbox_attachment_not_downloadable')}
													data-testid={`admin-inbox-attachment-disabled-${i}`}
												>
                          {label}
                        </span>
											);
										})}
									</div>
								</div>
							)}

							{selected.html ? (
								<div
									className="text-sm text-[#3a3a3a] leading-relaxed prose prose-sm max-w-none break-words"
									dangerouslySetInnerHTML={{ __html: selected.html }}
									data-testid="admin-inbox-html-body"
								/>
							) : (
								<pre className="text-sm text-[#3a3a3a] leading-relaxed whitespace-pre-wrap font-sans break-words"
										 data-testid="admin-inbox-text-body">
                  {selected.text || '(empty body)'}
                </pre>
							)}

							{/* Actions */}
							<div className="flex items-center gap-2 mt-6 pt-4 border-t border-[#E6E2D6]">
								<a
									href={`mailto:${selected.from_email}?subject=Re: ${encodeURIComponent(selected.subject || '')}`}
									className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-[#1A3C34] text-white
											hover:bg-[#153029]"
									data-testid="admin-inbox-reply-btn"
								>
									<Send className="w-3 h-3" /> {t('admin.inbox_reply')}
								</a>
								{selected.is_archived ? (
									<Button size="sm" variant="outline" onClick={() => toggleArchive(selected.id, false)}
													className="border-[#E6E2D6] text-[#1A3C34] rounded-sm h-8 text-xs"
													data-testid="admin-inbox-unarchive-btn">
										<ArchiveRestore className="w-3 h-3 mr-1" /> {t('admin.inbox_unarchive')}
									</Button>
								) : (
									<Button size="sm" variant="outline" onClick={() => toggleArchive(selected.id, true)}
													className="border-[#E6E2D6] text-[#1A3C34] rounded-sm h-8 text-xs"
													data-testid="admin-inbox-archive-btn">
										<Archive className="w-3 h-3 mr-1" /> {t('admin.inbox_archive')}
									</Button>
								)}
								<Button size="sm" variant="outline" onClick={() => toggleRead(selected.id, !selected.is_read)}
												className="border-[#E6E2D6] text-[#1A3C34] rounded-sm h-8 text-xs"
												data-testid="admin-inbox-toggle-read-btn">
									{selected.is_read ? <Mail className="w-3 h-3 mr-1" /> : <MailOpen className="w-3 h-3 mr-1" />}
									{selected.is_read ? t('admin.inbox_mark_unread') : t('admin.inbox_mark_read')}
								</Button>
								<Button
									size="sm"
									variant="outline"
									onClick={() => remove(selected.id)}
									className="border-[#E6E2D6] text-[#9B2C2C] rounded-sm h-8 w-8 p-0 ml-auto"
									title={t('common.delete')}
									data-testid="admin-inbox-delete-btn"
								>
									<Trash2 className="w-3 h-3" />
								</Button>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Webhook setup dialog */}
			<Dialog open={webhookOpen} onOpenChange={setWebhookOpen}>
				<DialogContent className="sm:max-w-xl" data-testid="admin-inbox-webhook-dialog">
					<DialogHeader>
						<DialogTitle className="text-[#1A3C34] font-['Playfair_Display'] flex items-center gap-2">
							<Webhook className="w-5 h-5 text-[#8B4513]" /> {t('admin.inbox_webhook_setup')}
						</DialogTitle>
					</DialogHeader>
					{webhookCfg ? (
						<div className="space-y-4 pt-2">
							<div
								className={`flex items-center gap-2 text-xs px-3 py-2 rounded-sm ${
									webhookCfg.configured ? 'bg-[#E6F0EC] text-[#1A3C34]' : 'bg-[#FBE7E7] text-[#9B2C2C]'
								}`}
								data-testid="admin-inbox-webhook-status"
							>
								{webhookCfg.configured ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
								{webhookCfg.configured ? t('admin.inbox_webhook_ok') : t('admin.inbox_webhook_missing')}
							</div>

							<div>
								<div className="text-[10px] uppercase tracking-wider text-[#666055]/70 mb-1">
									{t('admin.inbox_webhook_url')}
								</div>
								<div className="flex items-center gap-2 bg-[#F2F0E9] border border-[#E6E2D6] rounded-sm px-3 py-2">
									<code className="text-xs text-[#1A3C34] break-all flex-1"
												data-testid="admin-inbox-webhook-url">
										{webhookCfg.webhook_url}
									</code>
									<Button
										size="sm"
										variant="ghost"
										onClick={() => { navigator.clipboard.writeText(webhookCfg.webhook_url); toast.success(t('common.success')); }}
										className="h-7 px-2"
										data-testid="admin-inbox-webhook-copy"
									>
										<Copy className="w-3 h-3" />
									</Button>
								</div>
							</div>

							<div>
								<div className="text-[10px] uppercase tracking-wider text-[#666055]/70 mb-1">
									{t('admin.inbox_webhook_auth_options')}
								</div>
								<ul className="text-xs text-[#3a3a3a] space-y-1">
									{webhookCfg.header_options.map((h) => (
										<li key={h} className="font-mono bg-[#F2F0E9] border border-[#E6E2D6] rounded-sm px-3 py-1.5">{h}</li>
									))}
								</ul>
							</div>

							<p className="text-[11px] text-[#666055] leading-relaxed">
								{t('admin.inbox_webhook_hint')}
							</p>
						</div>
					) : (
						<div className="text-sm text-[#666055]">Loading...</div>
					)}
				</DialogContent>
			</Dialog>

			{/* Test email injection dialog */}
			<Dialog open={testOpen} onOpenChange={setTestOpen}>
				<DialogContent className="sm:max-w-lg" data-testid="admin-inbox-test-dialog">
					<DialogHeader>
						<DialogTitle className="text-[#1A3C34] font-['Playfair_Display'] flex items-center gap-2">
							<Send className="w-5 h-5 text-[#8B4513]" /> {t('admin.inbox_inject_test')}
						</DialogTitle>
					</DialogHeader>
					<form onSubmit={submitTest} className="space-y-3 pt-2">
						<div>
							<label className="text-[10px] uppercase tracking-wider text-[#666055]/70">{t('admin.inbox_from')}</label>
							<Input
								value={testForm.from_email}
								onChange={(e) => setTestForm({ ...testForm, from_email: e.target.value })}
								className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10 mt-1 text-sm"
								data-testid="admin-inbox-test-from"
							/>
						</div>
						<div>
							<label className="text-[10px] uppercase tracking-wider text-[#666055]/70">{t('admin.inbox_to')}</label>
							<Input
								value={testForm.to_email}
								onChange={(e) => setTestForm({ ...testForm, to_email: e.target.value })}
								className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10 mt-1 text-sm"
								data-testid="admin-inbox-test-to"
							/>
						</div>
						<div>
							<label className="text-[10px] uppercase tracking-wider text-[#666055]/70">{t('admin.inbox_subject')}</label>
							<Input
								value={testForm.subject}
								onChange={(e) => setTestForm({ ...testForm, subject: e.target.value })}
								className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10 mt-1 text-sm"
								data-testid="admin-inbox-test-subject"
								required
							/>
						</div>
						<div>
							<label className="text-[10px] uppercase tracking-wider text-[#666055]/70">{t('admin.inbox_body')}</label>
							<Textarea
								value={testForm.body}
								onChange={(e) => setTestForm({ ...testForm, body: e.target.value })}
								rows={6}
								className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm mt-1 text-sm"
								data-testid="admin-inbox-test-body"
								required
							/>
						</div>
						<div className="flex justify-end gap-2 pt-2">
							<Button type="button" variant="ghost" onClick={() => setTestOpen(false)} className="text-[#666055]">
								{t('common.cancel')}
							</Button>
							<Button
								type="submit"
								disabled={injecting || !testForm.subject.trim() || !testForm.body.trim()}
								className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm"
								data-testid="admin-inbox-test-submit"
							>
								{injecting ? t('common.loading') : t('admin.inbox_inject_test')}
							</Button>
						</div>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}